import { type IAgentInfo, type IAgentOperateConfig, type ILayerProps } from '@/version-types';

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;

/**
 * 创建代理人信息
 * @param options - 代理人信息配置
 * @param options.taskId - 任务ID
 * @param options.id - 代理人 ID，作为布局分组标记
 * @param options.name - 代理人名称
 * @param options.avatar - 代理人头像
 * @param options.relationID - 图层关联标记
 * @param options.downloadFileName - 下载文件名
 * @param options.disableRotate - 是否禁用旋转操作
 * @param options.disableScale - 是否禁用缩放操作
 * @param options.disableResize - 是否禁用调整大小操作
 * @param options.borderRadius - 操作框边框圆角
 * @returns 代理人信息
 */
export const createAgentInfo = ({
    taskId,
    id,
    name,
    avatar,
    relationID,
    downloadFileName,
    disableRotate,
    disableScale,
    disableResize,
    borderRadius,
}: {
    taskId: string;
    id: string;
    name: string;
    avatar: string;
    relationID?: string;
    downloadFileName?: string;
    disableRotate?: boolean;
    disableScale?: boolean;
    disableResize?: boolean;
    borderRadius?: number;
}): IAgentInfo => {
    return {
        /** 所属任务 */
        taskId,
        /** 代理人 ID */
        id,
        /** 代理人名称 */
        name,
        /** 代理人头像 */
        avatar,
        /** 图层关联标记 */
        relationID,
        /** 是否禁用基础操作 */
        disable: {
            /** 是否禁用旋转操作 */
            rotate: disableRotate,
            /** 是否禁用缩放操作 */
            scale: disableScale,
            /** 是否禁用调整大小操作 */
            resize: disableResize,
        },
        /** 操作框样式 */
        style: {
            /** 操作框边框圆角 */
            borderRadius,
        },
        /** 下载文件名 */
        downloadFileName,
    };
};

const hasValue = <T>(value: T | undefined): value is T => value !== undefined;

export const createDetachedAgentOperateConfig = (agent?: IAgentInfo): IAgentOperateConfig | undefined => {
    if (!agent) return undefined;

    const config: IAgentOperateConfig = {};
    const disable: IAgentOperateConfig['disable'] = {};

    const disableScale = agent.disable?.scale;
    const disableResize = agent.disable?.resize;
    const borderRadius = agent.style?.borderRadius;

    if (hasValue(disableScale)) {
        disable.scale = disableScale;
    }
    if (hasValue(disableResize)) {
        disable.resize = disableResize;
    }
    if (Object.keys(disable).length) {
        config.disable = disable;
    }

    if (hasValue(borderRadius)) {
        config.style = {
            borderRadius,
        };
    }

    if (hasValue(agent.forceCustomOperate)) {
        config.forceCustomOperate = agent.forceCustomOperate;
    }

    return Object.keys(config).length ? config : undefined;
};

export const getLayerAgentOperateConfig = (layer?: ILayerProps | null) => {
    return layer?.extraInfo?.agent || layer?.extraInfo?.agentOperateConfig;
};

/**
 * 将字节单位的文件大小格式化为可读文本
 * @param size - 文件大小，单位字节
 * @returns 格式化后的文件大小
 */
export function formatSize(size: number) {
    if (size >= BYTES_PER_MB) {
        const sizeInMb = size / BYTES_PER_MB;
        return `${Number(sizeInMb.toFixed(2))}MB`;
    }

    return `${Math.max(1, Math.round(size / BYTES_PER_KB))}KB`;
}
